import { atom, type getDefaultStore } from 'jotai';

// "The Sharma Family was deleted by Asha." (M54, D189): shown on the
// waiting-for-a-family screen when that was someone's last family, and kept
// on the device until dismissed, so a reload still says what happened.

export interface FamilyDeletedNote {
  familyId: string;
  familyName: string;
  /** Empty when the app found out later and doesn't know who (D191). */
  deletedByName: string;
}

const KEY = 'twinden:familyDeleted:';

/** The notes for the signed-in person (loaded by `loadDeletedNotes`). */
export const familyDeletedNotesAtom = atom<FamilyDeletedNote[]>([]);

type Store = ReturnType<typeof getDefaultStore>;

function read(userId: string): FamilyDeletedNote[] {
  try {
    const raw = localStorage.getItem(KEY + userId);
    const parsed = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (note): note is FamilyDeletedNote =>
            !!note &&
            typeof note.familyId === 'string' &&
            typeof note.familyName === 'string' &&
            typeof note.deletedByName === 'string'
        )
      : [];
  } catch {
    return [];
  }
}

function write(userId: string, notes: FamilyDeletedNote[]) {
  try {
    if (notes.length === 0) localStorage.removeItem(KEY + userId);
    else localStorage.setItem(KEY + userId, JSON.stringify(notes));
  } catch {
    // Private mode: the note shows until the next reload.
  }
}

export function loadDeletedNotes(userId: string, store: Store) {
  store.set(familyDeletedNotesAtom, read(userId));
}

export function addDeletedNote(
  userId: string,
  note: FamilyDeletedNote,
  store: Store
) {
  const notes = [
    ...read(userId).filter((existing) => existing.familyId !== note.familyId),
    note,
  ];
  write(userId, notes);
  store.set(familyDeletedNotesAtom, notes);
}

export function dismissDeletedNote(
  userId: string,
  familyId: string,
  store: Store
) {
  const notes = read(userId).filter((note) => note.familyId !== familyId);
  write(userId, notes);
  store.set(familyDeletedNotesAtom, notes);
}

/** Signing out: the notes are this person's, not the device's. */
export function forgetDeletedNotes(userId: string) {
  write(userId, []);
  try {
    localStorage.removeItem(KNOWN + userId);
  } catch {
    // Nothing kept.
  }
}

// The families this account was last seen in, on this device (D191): a
// family missing from the next list was deleted (or this person removed)
// while the device was away, and is forgotten here the same way.

const KNOWN = 'twinden:knownFamilies:';

export interface KnownFamily {
  id: string;
  name: string;
}

/** Null when this device hasn't recorded a list yet (nothing to compare). */
export function readKnownFamilies(userId: string): KnownFamily[] | null {
  try {
    const raw = localStorage.getItem(KNOWN + userId);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter(
          (family): family is KnownFamily =>
            !!family &&
            typeof family.id === 'string' &&
            typeof family.name === 'string'
        )
      : null;
  } catch {
    return null;
  }
}

export function writeKnownFamilies(userId: string, families: KnownFamily[]) {
  try {
    localStorage.setItem(
      KNOWN + userId,
      JSON.stringify(families.map(({ id, name }) => ({ id, name })))
    );
  } catch {
    // Private mode: no catch-up after a reload, the socket still works.
  }
}

/** Left or deleted here: not something to catch up on later. */
export function forgetKnownFamily(userId: string, familyId: string) {
  const known = readKnownFamilies(userId);
  if (known)
    writeKnownFamilies(
      userId,
      known.filter((family) => family.id !== familyId)
    );
}
